// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useEffect, useRef } from 'react';

/**
 * A React hook that manages a window timeout. This hook returns a function to
 * start the timeout.
 *
 * @param callback - The callback to execute when the timeout expires.
 * @param delay - The delay in milliseconds before the timeout expires.
 */
export function useTimeout(callback: () => void, delay: number) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const start = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(callback, delay);
  };

  return { start };
}
